import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { visuallyHidden } from '../../lib/visually-hidden';
import { cssVar } from '../../theme/tokens';
import { iconSize, mergeSx } from './sizes';

export interface FilterChipProps {
  label: ReactNode;
  selected: boolean;
  onClick: () => void;
  /** `toggle` shows check when selected and add when not (provinces); `plain` has no icon. */
  variant?: 'plain' | 'toggle';
  /** A custom leading icon (overrides the toggle icons). */
  icon?: ReactNode;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * A pill that switches one filter or option on and off (categories, provinces). A toggle button:
 * `aria-pressed` says whether it is on. Selected chips are gold with navy text; the outline of
 * an unselected chip uses the input border colour, which passes 3:1.
 */
export function FilterChip({
  label,
  selected,
  onClick,
  variant = 'plain',
  icon,
  disabled = false,
  sx,
}: FilterChipProps) {
  const leading =
    icon ?? (variant === 'toggle' ? selected ? <CheckRoundedIcon /> : <AddRoundedIcon /> : null);
  return (
    <ButtonBase
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      sx={mergeSx(
        {
          gap: 1.5,
          height: cssVar('control-h-sm'),
          px: 3,
          borderRadius: cssVar('radius-pill'),
          border: '1px solid',
          borderColor: selected ? cssVar('primary') : cssVar('border-strong'),
          bgcolor: selected ? cssVar('primary') : 'transparent',
          color: selected ? cssVar('text-on-primary') : cssVar('text'),
          typography: 'caption',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: selected ? cssVar('primary-hover') : cssVar('hover') },
          '&.Mui-disabled': { opacity: 0.5 },
          '& .MuiSvgIcon-root': { fontSize: iconSize('sm') },
        },
        sx,
      )}
    >
      {leading}
      {label}
    </ButtonBase>
  );
}

export interface FilterChipGroupProps {
  /** Names the group for screen readers ("Category", "Quick ranges"); not shown. */
  label: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/** A row of filter chips that wraps, named by a hidden legend (a real `fieldset`). */
export function FilterChipGroup({ label, children, sx }: FilterChipGroupProps) {
  return (
    <Box
      component="fieldset"
      sx={mergeSx(
        {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          m: 0,
          p: 0,
          border: 0,
          minWidth: 0,
        },
        sx,
      )}
    >
      <legend style={visuallyHidden}>{label}</legend>
      {children}
    </Box>
  );
}
